/**
 * @packageDocumentation NewFeatureBadge
 *
 * A "new feature" badge is active when the user hasn't hidden it yet or is still under the shown-count threshold.
 *
 * `useDismissNewFeatureBadge` hides the badge — typically called when the user visits the feature's page,
 * since that's a natural signal they've seen it.
 * `useShowNewFeatureBadge` reads whether to render the badge.
 *
 * Hiding it isn't permanent: on logout (`useUpdateNewFeatureBadgeState`), every hidden badge is
 * reactivated, so the user sees it again next session. A badge's shown count only increments once it
 * had already passed the max-shows threshold when hidden — that's what lets it eventually stop
 * reappearing, instead of resetting forever every logout.
 */
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

export enum NewFeatureName {
  F24 = 'f24',
  AiChat = 'aichat',
}

interface NewFeatureState {
  dismissed: boolean;
  shownCount: number;
}

type NewFeaturesStorage = Partial<Record<NewFeatureName, NewFeatureState>>;

const storage = createJSONStorage<NewFeaturesStorage>(() => localStorage);

const newFeatureAtom = atomWithStorage<NewFeaturesStorage>('new-feature-badge', {}, storage);

const DEFAULT_MAX_SHOWS = 3;

const MAX_SHOWS_OVERRIDES: Partial<Record<NewFeatureName, number>> = {
  [NewFeatureName.AiChat]: 10,
};

const getMaxShows = (featureName: NewFeatureName) => MAX_SHOWS_OVERRIDES[featureName] ?? DEFAULT_MAX_SHOWS;

const showAtomFamily = atomFamily((featureName: NewFeatureName) =>
  atom((get) => {
    const features = get(newFeatureAtom);
    const feature = features[featureName];
    const maxShows = getMaxShows(featureName);

    if (!feature) return true;
    if (feature.dismissed) return false;
    if (feature.shownCount >= maxShows) return false;

    return true;
  }),
);

export const useShowNewFeatureBadge = (featureName: NewFeatureName) => useAtomValue(showAtomFamily(featureName));

const dismissAtomFamily = atomFamily((featureName: NewFeatureName) =>
  atom(null, (_get, set) => {
    set(newFeatureAtom, (prev) => ({
      ...prev,
      [featureName]: {
        dismissed: (prev[featureName]?.shownCount ?? 0) < getMaxShows(featureName) ? true : false,
        shownCount: prev[featureName]?.shownCount ?? 0,
      },
    }));
  }),
);

export const useDismissNewFeatureBadge = (featureName: NewFeatureName) => {
  return useSetAtom(dismissAtomFamily(featureName));
};

const resetDismissedOnLogoutAtom = atom(null, (_get, set) => {
  set(newFeatureAtom, (prev) =>
    Object.fromEntries(
      Object.values(NewFeatureName).map((featureName) => {
        const feature: NewFeatureState = prev[featureName] ?? { dismissed: false, shownCount: 0 };

        return [
          featureName,
          {
            dismissed: false,
            shownCount:
              feature.dismissed && feature.shownCount < getMaxShows(featureName)
                ? feature.shownCount + 1
                : feature.shownCount,
          },
        ];
      }),
    ),
  );
});

export const useUpdateNewFeatureBadgeState = () => {
  const updateNewFeatureBadgeState = useSetAtom(resetDismissedOnLogoutAtom);
  return updateNewFeatureBadgeState;
};
